/**
 * Formats a number to a decimal style string with the given precision, respecting the locale of the browser.
 *
 * @param n - The number to format
 * @param precision - The number of fractional digits to include, defaults to 0
 * @returns The formatted number, or 0 if `n` is null or undefined
 */
export const formatToDecimalString = (n: number | null | undefined, precision = 0) => {
  const [locale] = navigator.language.split('@');
  return new Intl.NumberFormat(locale, { maximumFractionDigits: precision }).format(n ?? 0);
};

/**
 * Formats a number to a unit style string with the given unit, respecting the locale of the browser.
 * Numbers with decimals are rounded to the nearest integer.
 * @param n - The number to format
 * @param unit - The unit to format the number to, e.g., 'meter', 'liter', 'byte'.
 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/supportedValuesOf#supported_unit_identifiers
 * @returns The formatted number, or 0 if `n` is null or undefined
 */
export const formatToUnitString = (n: number | null | undefined, unit: Intl.NumberFormatOptions['unit']): string => {
  const [locale] = navigator.language.split('@');
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: 'long',
    maximumFractionDigits: 0,
  }).format(n ?? 0);
};

/**
 * Formats a number to a percent style string with the given precision, respecting the locale of the browser.
 * Numbers with decimals are rounded to the nearest integer.
 * @param n - The number to format
 * @param precision - The number of fractional digits to include, defaults to 0
 * @returns The formatted number, or 0 if `n` is null or undefined
 */
export const formatToPercentString = (n: number | null | undefined, precision = 0): string => {
  const [locale] = navigator.language.split('@');
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: precision,
  }).format(n ?? 0);
};

/**
 * Formats a number to a compact form style string with the given precision, respecting the locale of the browser.
 *
 * Examples:
 * `1.2e6 = 1.200.000 => 1.2M`
 * `1.2e9 = 1.200.000.000 => 1.2B`
 *
 * @param n - The number to format
 * @param precision - The number of fractional digits to include, defaults to 0
 * @returns The formatted number, or 0 if `n` is null or undefined
 */
export const formatToCompactNotationString = (n: number | null | undefined, precision = 0): string => {
  const [locale] = navigator.language.split('@');
  return Intl.NumberFormat(locale, {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: precision,
  }).format(n ?? 0);
};
